import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import {
  QUERY_SEARCH_MC_CHANGES,
  QUERY_SEARCH_MC_CHANGES_INFINITE,
  useCreateMcChange,
  useGetMcChangeById,
  useUpdateMcChange
} from '@apps-common/apis/portal/mc-changes'
import {
  MonitoringChangeCompanyForm,
  MonitoringChangeConfirmationForm,
  MonitoringChangeDetailsForm,
  MonitoringChangeUnitForm
} from '@apps-common/apps/portal/pages/service-requests.old/pages/monitoring-change-modify/components'
import { VertSpacerLg, VertSpacerXl } from '@apps-common/components/layout'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { queryClient } from '@apps-common/lib'
import { IMcChangeServiceRequestModel } from '@apps-common/types/portal/service-request'

export enum ModifyMcChangeStep {
  unit = 'unit',
  companies = 'companies',
  details = 'details',
  confirm = 'confirm',
  submit = 'submit'
}

export type stepDirection = 'forward' | 'backward'

interface IProps {
  mcChangeId?: number
  unitId?: string
}

export const MonitoringChangeModifyView: React.FC<IProps> = ({ mcChangeId, unitId }) => {
  const [step, setStep] = useState<ModifyMcChangeStep>(ModifyMcChangeStep.unit)
  const { navigateBack } = useNavigation()

  const isEditing = mcChangeId !== undefined

  const moveStep = (direction: stepDirection) => {
    if (direction === 'forward') {
      if (step === ModifyMcChangeStep.unit) {
        setStep(ModifyMcChangeStep.companies)

        return ModifyMcChangeStep.companies
      }
      if (step === ModifyMcChangeStep.companies) {
        setStep(ModifyMcChangeStep.details)

        return ModifyMcChangeStep.details
      }
      if (step === ModifyMcChangeStep.details) {
        setStep(ModifyMcChangeStep.confirm)

        return ModifyMcChangeStep.confirm
      }
      if (step === ModifyMcChangeStep.confirm) {
        return ModifyMcChangeStep.submit
      }
    } else if (direction === 'backward') {
      if (step === ModifyMcChangeStep.confirm) {
        setStep(ModifyMcChangeStep.details)

        return ModifyMcChangeStep.details
      }
      if (step === ModifyMcChangeStep.details) {
        setStep(ModifyMcChangeStep.companies)

        return ModifyMcChangeStep.companies
      }
      if (step === ModifyMcChangeStep.companies) {
        setStep(ModifyMcChangeStep.unit)

        return ModifyMcChangeStep.unit
      }
    }

    return step
  }

  const {
    isLoading: mcChangeIsLoading,
    isError: mcChangeIsError,
    refetch: mcChangeRefetch,
    data: mcChange
  } = useGetMcChangeById({
    config: { enabled: isEditing },
    id: mcChangeId || 0
  })

  const onSave = (data: IMcChangeServiceRequestModel) => {
    queryClient.invalidateQueries({ queryKey: QUERY_SEARCH_MC_CHANGES() })
    queryClient.invalidateQueries({ queryKey: QUERY_SEARCH_MC_CHANGES_INFINITE() })
    // navigateBack({ routeUrl: ROUTE_SERVICE_REQUESTS.buildPath(), route: ROUTE_SERVICE_REQUESTS })
  }

  const { mutate: createMcChange, isPending: createMcChangeIsPending } = useCreateMcChange(true, (data) => onSave(data))
  const { mutate: updateMcChange, isPending: updateMcChangeIsPending } = useUpdateMcChange(true, (data) => onSave(data))

  const methods = useForm<IMcChangeServiceRequestModel>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })
  const { handleSubmit, reset, setValue, formState } = methods

  useEffect(() => {
    setValue('isOurUnit', true)
  }, [])

  useEffect(() => {
    if (mcChange) {
      reset(mcChange)
    }
  }, [mcChange])

  const onSubmit = async (values: IMcChangeServiceRequestModel) => {
    const nextStep = moveStep('forward')

    if (nextStep !== ModifyMcChangeStep.submit) return

    const updatedValues = { ...values }

    if (updatedValues.newBureauId?.length > 0) {
      updatedValues.assignBureau = true
    }

    if (updatedValues.techId?.length > 0) {
      updatedValues.assignTech = true
    }

    if (updatedValues.contactName?.length > 0 && updatedValues.contactPhone?.length > 0) {
      updatedValues.hasAssignedContact = true
    }

    if (!isEditing) {
      createMcChange({ model: updatedValues })
    } else {
      updateMcChange({ model: updatedValues })
    }
  }

  const loading = mcChangeIsLoading || mcChangeIsError
  const updating = createMcChangeIsPending || updateMcChangeIsPending

  return (
    <div className='max-w-[800px]'>
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          {/* step 1 - unit */}
          <MonitoringChangeUnitForm loading={loading} updating={updating} step={step} moveStep={moveStep} defaultUnitId={unitId} />
          <VertSpacerLg />
          {/* step 2 - companies */}
          <MonitoringChangeCompanyForm loading={loading} updating={updating} step={step} moveStep={moveStep} />
          <VertSpacerLg />
          {/* step 3 - contact details */}
          <MonitoringChangeDetailsForm loading={loading} updating={updating} step={step} moveStep={moveStep} />
          <VertSpacerLg />
          {/* step 4 - confirm */}
          <MonitoringChangeConfirmationForm loading={loading} updating={updating} step={step} moveStep={moveStep} />
        </form>
      </FormProvider>
      <VertSpacerXl />
    </div>
  )
}
